/**
 * ApiClient — Unified Data Access Layer
 *
 * LOCAL mode  → calls PostgREST on http://localhost:3001
 * SUPABASE    → calls Supabase REST (swap VITE_BACKEND_MODE=supabase)
 *
 * All secrets come from .env — NOTHING is hardcoded here.
 */

const BACKEND_MODE = import.meta.env.VITE_BACKEND_MODE || 'local';
const API_URL      = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DbUser {
  id: string;
  username: string;
  email: string;
  role: 'user' | 'admin';
  player_tag: string;
  avatar_id: string;
  google_id?: string | null;
  created_at?: string;
  last_seen?: string;
}

export interface ScoreRow {
  id?: number;
  user_id: string;
  game_id: string;
  score: number;
  session_ms?: number;
  achieved_at?: string;
  username?: string;   // joined from profiles
  avatar_id?: string;  // joined from profiles
}

export interface AchievementRow {
  id?: number;
  user_id: string;
  game_id: string;
  achievement: string;
  unlocked_at?: string;
}

export interface UserSessionRow {
  id?: number;
  user_id: string;
  game_id: string;
  duration_ms: number;
  browser?: string;
  device_type?: string;
  os?: string;
  started_at?: string;
}

export interface GuestStatRow {
  id?: number;
  session_token: string;
  game_id: string;
  duration_ms: number;
  browser?: string;
  device_type?: string;
  os?: string;
  played_at?: string;
}

export interface ApiResult<T> {
  data: T | null;
  error: string | null;
}

// ─── Token Storage ───────────────────────────────────────────────────────────

const TOKEN_KEY = 'arcade_jwt';

function saveToken(token: string) {
  sessionStorage.setItem(TOKEN_KEY, token);
}
function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}
function clearToken() {
  sessionStorage.removeItem(TOKEN_KEY);
}

// ─── HTTP Helpers ─────────────────────────────────────────────────────────────

async function post<T>(path: string, body: unknown, auth = false): Promise<ApiResult<T>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json' };
  if (auth) {
    const tok = getToken();
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
  }
  try {
    const res = await fetch(`${API_URL}${path}`, { method: 'POST', headers, body: JSON.stringify(body) });
    const json = await res.json();
    if (!res.ok || json?.error) return { data: null, error: json?.error || 'Sunucu hatası' };
    return { data: json as T, error: null };
  } catch {
    return { data: null, error: 'Bağlantı hatası — Docker çalışıyor mu? (docker-compose up -d)' };
  }
}

async function get<T>(path: string, params?: Record<string, string>, auth = false): Promise<ApiResult<T>> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (auth) {
    const tok = getToken();
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
  }
  const query = params ? '?' + new URLSearchParams(params).toString() : '';
  try {
    const res = await fetch(`${API_URL}${path}${query}`, { headers });
    const json = await res.json();
    if (!res.ok) return { data: null, error: (json as { message?: string })?.message || 'Sorgu hatası' };
    return { data: json as T, error: null };
  } catch {
    return { data: null, error: 'Bağlantı hatası — Docker çalışıyor mu? (docker-compose up -d)' };
  }
}

// ─── PostgREST RPC Helper ─────────────────────────────────────────────────────

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<ApiResult<T>> {
  return post<T>(`/rpc/${fn}`, args);
}

// ─── Auth API ────────────────────────────────────────────────────────────────

export const ApiClient = {
  // Generates a simple JWT-like session token (for local mode only)
  // In production (Supabase), real JWT is returned by Supabase Auth
  _generateLocalToken(userId: string, role: string): string {
    const header  = btoa(JSON.stringify({ alg: 'none', typ: 'JWT' }));
    const payload = btoa(JSON.stringify({ sub: userId, role, iat: Date.now() }));
    return `${header}.${payload}.local_sig`;
  },

  async login(email: string, password: string): Promise<ApiResult<DbUser>> {
    if (BACKEND_MODE === 'supabase') {
      // ── Supabase path (production) ──────────────────────────────────────────
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { data: null, error: error.message };

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', data.user!.id)
        .single();

      return { data: profile as DbUser, error: null };
    }

    // ── Local Docker path ───────────────────────────────────────────────────
    const result = await rpc<DbUser>('arcade_login', {
      p_email: email.toLowerCase(),
      p_password: password,
    });

    if (result.error) return result;

    // Save lightweight local session token
    const tok = this._generateLocalToken(result.data!.id, result.data!.role);
    saveToken(tok);
    return result;
  },

  async register(email: string, password: string, username: string): Promise<ApiResult<DbUser>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const tag = '#' + Math.floor(1000 + Math.random() * 9000);
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) return { data: null, error: error.message };

      const { data: profile } = await supabase
        .from('profiles')
        .insert({ id: data.user!.id, username, email, role: 'user', player_tag: tag, avatar_id: 'av_3' })
        .select()
        .single();

      return { data: profile as DbUser, error: null };
    }

    // Local Docker
    const result = await rpc<DbUser>('arcade_register', {
      p_email: email.toLowerCase(),
      p_password: password,
      p_username: username,
    });
    if (result.error) return result;

    const tok = this._generateLocalToken(result.data!.id, 'user');
    saveToken(tok);
    return result;
  },

  async loginWithGoogle(): Promise<ApiResult<DbUser>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin },
      });
      if (error) return { data: null, error: error.message };
      return { data: null, error: null }; // redirect in progress
    }
    // Local mode — Google OAuth needs Supabase, show info
    return { data: null, error: 'Google OAuth yalnızca production (Supabase) modunda çalışır.' };
  },

  logout() {
    clearToken();
    if (BACKEND_MODE === 'supabase') {
      import('./supabase').then(({ supabase }) => supabase.auth.signOut());
    }
  },

  // ─── Scores ────────────────────────────────────────────────────────────────

  async getTopScores(gameId: string, limit = 10): Promise<ApiResult<ScoreRow[]>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      let query = supabase
        .from('scores')
        .select('*, profiles(username, avatar_id)')
        .order('score', { ascending: false })
        .limit(limit);
        
      if (gameId !== 'all') {
        query = query.eq('game_id', gameId);
      }
      
      const { data, error } = await query;
      if (error) return { data: null, error: error.message };
      const mapped = (data as Array<{ user_id: string; game_id: string; score: number; session_ms?: number; achieved_at?: string; profiles: { username: string; avatar_id: string } }>).map((r) => ({
        ...r,
        username: r.profiles?.username,
        avatar_id: r.profiles?.avatar_id,
      }));
      return { data: mapped as ScoreRow[], error: null };
    }
    // PostgREST: join via profiles
    const params: Record<string, string> = {
      order:   'score.desc',
      limit:   String(limit),
      select:  'id,user_id,game_id,score,session_ms,achieved_at,profiles(username,avatar_id)',
    };
    if (gameId !== 'all') {
      params.game_id = `eq.${gameId}`;
    }
    return get<ScoreRow[]>('/scores', params);
  },

  async submitScore(userId: string, gameId: string, score: number, sessionMs?: number): Promise<ApiResult<ScoreRow>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase
        .from('scores')
        .insert({ user_id: userId, game_id: gameId, score, session_ms: sessionMs })
        .select()
        .single();
      if (error) return { data: null, error: error.message };
      return { data: data as ScoreRow, error: null };
    }
    return post<ScoreRow>('/scores', { user_id: userId, game_id: gameId, score, session_ms: sessionMs }, true);
  },

  async getUserScores(userId: string): Promise<ApiResult<ScoreRow[]>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase
        .from('scores')
        .select('*')
        .eq('user_id', userId)
        .order('achieved_at', { ascending: false });
      if (error) return { data: null, error: error.message };
      return { data: data as ScoreRow[], error: null };
    }
    return get<ScoreRow[]>('/scores', { user_id: `eq.${userId}`, order: 'achieved_at.desc' }, false);
  },

  // ─── Achievements ──────────────────────────────────────────────────────────

  async getUserAchievements(userId: string): Promise<ApiResult<AchievementRow[]>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase
        .from('achievements')
        .select('*')
        .eq('user_id', userId)
        .order('unlocked_at', { ascending: false });
      if (error) return { data: null, error: error.message };
      return { data: data as AchievementRow[], error: null };
    }
    return get<AchievementRow[]>('/achievements', { user_id: `eq.${userId}`, order: 'unlocked_at.desc' }, true);
  },

  async unlockAchievement(userId: string, gameId: string, achievement: string): Promise<ApiResult<AchievementRow>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase
        .from('achievements')
        .upsert({ user_id: userId, game_id: gameId, achievement }, { onConflict: 'user_id,game_id,achievement' })
        .select()
        .single();
      if (error) return { data: null, error: error.message };
      return { data: data as AchievementRow, error: null };
    }
    return post<AchievementRow>('/achievements', { user_id: userId, game_id: gameId, achievement }, true);
  },

  // ─── Feedback ──────────────────────────────────────────────────────────────
  
  async submitFeedback(userId: string | undefined, gameId: string, message: string): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase
        .from('feedbacks')
        .insert({ user_id: userId || null, game_id: gameId, message });
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    return post<null>('/feedbacks', { user_id: userId || null, game_id: gameId, message }, !!userId);
  },

  async getFeedbacks(): Promise<ApiResult<any[]>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase.from('feedbacks').select('*, profiles(username, email)').order('created_at', { ascending: false });
      if (error) return { data: null, error: error.message };
      const mapped = (data as any[]).map((r) => ({ ...r, username: r.profiles?.username, email: r.profiles?.email }));
      return { data: mapped, error: null };
    }
    const res = await get<any[]>('/feedbacks', { select: '*,profiles(username,email)', order: 'created_at.desc' }, true);
    if (res.error) return { data: null, error: res.error };
    const mapped = (res.data || []).map((r) => ({ ...r, username: r.profiles?.username, email: r.profiles?.email }));
    return { data: mapped, error: null };
  },

  async deleteFeedback(id: number): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('feedbacks').delete().eq('id', id);
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    const headers: Record<string, string> = { Accept: 'application/json' };
    const tok = getToken();
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
    try {
      await fetch(`${API_URL}/feedbacks?id=eq.${id}`, { method: 'DELETE', headers });
      return { data: null, error: null };
    } catch {
      return { data: null, error: 'Silinemedi' };
    }
  },

  async deleteMultipleFeedbacks(ids: number[]): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('feedbacks').delete().in('id', ids);
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    const headers: Record<string, string> = { Accept: 'application/json' };
    const tok = getToken();
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
    try {
      await fetch(`${API_URL}/feedbacks?id=in.(${ids.join(',')})`, { method: 'DELETE', headers });
      return { data: null, error: null };
    } catch {
      return { data: null, error: 'Toplu silinemedi' };
    }
  },

  async deleteAllFeedbacks(): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('feedbacks').delete().neq('id', 0);
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    const headers: Record<string, string> = { Accept: 'application/json' };
    const tok = getToken();
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
    try {
      await fetch(`${API_URL}/feedbacks?id=gt.0`, { method: 'DELETE', headers });
      return { data: null, error: null };
    } catch {
      return { data: null, error: 'Tümü silinemedi' };
    }
  },

  // ─── Ratings ───────────────────────────────────────────────────────────────
  
  async getGameRating(gameId: string): Promise<ApiResult<{ average: number; count: number }>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase.from('game_ratings').select('rating').eq('game_id', gameId);
      if (error) return { data: null, error: error.message };
      if (!data || data.length === 0) return { data: { average: 0, count: 0 }, error: null };
      const avg = data.reduce((acc, curr) => acc + curr.rating, 0) / data.length;
      return { data: { average: avg, count: data.length }, error: null };
    }
    const res = await get<{rating: number}[]>('/game_ratings', { game_id: `eq.${gameId}`, select: 'rating' });
    if (res.error) return { data: null, error: res.error };
    const data = res.data || [];
    if (data.length === 0) return { data: { average: 0, count: 0 }, error: null };
    const avg = data.reduce((acc, curr) => acc + curr.rating, 0) / data.length;
    return { data: { average: avg, count: data.length }, error: null };
  },

  async submitRating(userId: string, gameId: string, rating: number): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('game_ratings').upsert({ user_id: userId, game_id: gameId, rating }, { onConflict: 'user_id,game_id' });
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    // PostgREST upsert requires Prefer: resolution=merge-duplicates which is tricky in our helper.
    // We can just use a standard POST since we defined UNIQUE constraint. 
    // Wait, POST will fail if it exists. PostgREST allows upsert with POST if Prefer: resolution=merge-duplicates.
    // Let's use our raw fetch here to support upsert.
    const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json', 'Prefer': 'resolution=merge-duplicates' };
    const tok = getToken();
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
    try {
      const r = await fetch(`${API_URL}/game_ratings`, { method: 'POST', headers, body: JSON.stringify({ user_id: userId, game_id: gameId, rating }) });
      if (!r.ok) return { data: null, error: 'Oylama başarısız' };
      return { data: null, error: null };
    } catch {
      return { data: null, error: 'Oylama başarısız' };
    }
  },

  // ─── Comments ──────────────────────────────────────────────────────────────
  
  async getComments(gameId: string): Promise<ApiResult<{ id: number; comment: string; created_at: string; username?: string; avatar_id?: string }[]>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase.from('game_comments').select('*, profiles(username, avatar_id)').eq('game_id', gameId).order('created_at', { ascending: false });
      if (error) return { data: null, error: error.message };
      const mapped = (data as any[]).map((r) => ({ ...r, username: r.profiles?.username, avatar_id: r.profiles?.avatar_id }));
      return { data: mapped, error: null };
    }
    const res = await get<any[]>('/game_comments', { game_id: `eq.${gameId}`, select: 'id,comment,created_at,profiles(username,avatar_id)', order: 'created_at.desc' });
    if (res.error) return { data: null, error: res.error };
    const mapped = (res.data || []).map((r) => ({ ...r, username: r.profiles?.username, avatar_id: r.profiles?.avatar_id }));
    return { data: mapped, error: null };
  },

  async postComment(userId: string, gameId: string, comment: string): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('game_comments').insert({ user_id: userId, game_id: gameId, comment });
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    return post<null>('/game_comments', { user_id: userId, game_id: gameId, comment }, true);
  },

  // ─── Profile ───────────────────────────────────────────────────────────────

  async getProfile(userId: string): Promise<ApiResult<DbUser>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single();
      if (error) return { data: null, error: error.message };
      return { data: data as DbUser, error: null };
    }
    const result = await get<DbUser[]>('/profiles', { id: `eq.${userId}`, select: '*' }, true);
    return { data: result.data?.[0] ?? null, error: result.error };
  },

  async updateAvatar(userId: string, avatarId: string): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('profiles').update({ avatar_id: avatarId }).eq('id', userId);
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    // PATCH via PostgREST
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Prefer: 'return=minimal',
    };
    const tok = getToken();
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
    try {
      await fetch(`${API_URL}/profiles?id=eq.${userId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ avatar_id: avatarId }),
      });
      return { data: null, error: null };
    } catch {
      return { data: null, error: 'Avatar güncellenemedi' };
    }
  },

  // ─── Admin Users ───────────────────────────────────────────────────────────
  
  async getUsers(): Promise<ApiResult<DbUser[]>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase.from('profiles').select('*').order('created_at', { ascending: false });
      if (error) return { data: null, error: error.message };
      return { data: data as DbUser[], error: null };
    }
    const res = await get<DbUser[]>('/profiles', { order: 'created_at.desc' }, true);
    return res;
  },

  async updateUserRole(userId: string, role: 'user' | 'admin'): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('profiles').update({ role }).eq('id', userId);
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json' };
    const tok = getToken();
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
    try {
      await fetch(`${API_URL}/profiles?id=eq.${userId}`, { method: 'PATCH', headers, body: JSON.stringify({ role }) });
      return { data: null, error: null };
    } catch {
      return { data: null, error: 'Güncellenemedi' };
    }
  },

  async deleteUser(userId: string): Promise<ApiResult<null>> {
    // Requires Supabase Edge Function to delete from Auth, but we can delete from Profiles (CASCADE will be handled if Auth triggers profile deletion, but we need to delete Auth).
    // In our simplified version, deleting profile might be enough for a demo, but realistically Auth deletion requires backend admin key.
    // For now we'll just delete from profiles.
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('profiles').delete().eq('id', userId);
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    const headers: Record<string, string> = { Accept: 'application/json' };
    const tok = getToken();
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
    try {
      await fetch(`${API_URL}/profiles?id=eq.${userId}`, { method: 'DELETE', headers });
      return { data: null, error: null };
    } catch {
      return { data: null, error: 'Silinemedi' };
    }
  },

  // ─── System Errors ─────────────────────────────────────────────────────────

  async logSystemError(message: string, stack: string = '', url: string = ''): Promise<ApiResult<null>> {
    const userJson = localStorage.getItem('arcade_auth_store');
    let userId = null;
    if (userJson) {
      try {
        const parsed = JSON.parse(userJson);
        if (parsed.state && parsed.state.user) userId = parsed.state.user.id;
      } catch (e) {}
    }
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      await supabase.from('system_errors').insert({ user_id: userId, message, stack, url });
      return { data: null, error: null };
    }
    return post<null>('/system_errors', { user_id: userId, message, stack, url }, false);
  },

  async getSystemErrors(): Promise<ApiResult<any[]>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase.from('system_errors').select('*').order('created_at', { ascending: false });
      if (error) return { data: null, error: error.message };
      return { data: data as any[], error: null };
    }
    return get<any[]>('/system_errors', { order: 'created_at.desc' }, true);
  },

  async deleteSystemError(id: number): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('system_errors').delete().eq('id', id);
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    const headers: Record<string, string> = { Accept: 'application/json' };
    const tok = getToken();
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
    try {
      await fetch(`${API_URL}/system_errors?id=eq.${id}`, { method: 'DELETE', headers });
      return { data: null, error: null };
    } catch {
      return { data: null, error: 'Silinemedi' };
    }
  },

  async deleteMultipleSystemErrors(ids: number[]): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('system_errors').delete().in('id', ids);
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    const headers: Record<string, string> = { Accept: 'application/json' };
    const tok = getToken();
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
    try {
      await fetch(`${API_URL}/system_errors?id=in.(${ids.join(',')})`, { method: 'DELETE', headers });
      return { data: null, error: null };
    } catch {
      return { data: null, error: 'Toplu silinemedi' };
    }
  },

  async deleteAllSystemErrors(): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('system_errors').delete().neq('id', 0); // Delete all by matching neq 0 (all IDs are > 0)
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    const headers: Record<string, string> = { Accept: 'application/json' };
    const tok = getToken();
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
    try {
      await fetch(`${API_URL}/system_errors?id=gt.0`, { method: 'DELETE', headers });
      return { data: null, error: null };
    } catch {
      return { data: null, error: 'Tümü silinemedi' };
    }
  },

  // ─── Friends ───────────────────────────────────────────────────────────────

  async getFriends(userId: string): Promise<ApiResult<any[]>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase
        .from('friends')
        .select('friend_id, profiles!friends_friend_id_fkey(id, username, player_tag, avatar_id, last_seen)')
        .eq('user_id', userId)
        .eq('status', 'accepted');
      if (error) return { data: null, error: error.message };
      return { data: data.map(d => d.profiles), error: null };
    }
    // PostgREST
    const res = await get<any[]>('/friends', { user_id: `eq.${userId}`, status: 'eq.accepted', select: 'friend_id,profiles!friends_friend_id_fkey(id,username,player_tag,avatar_id,last_seen)' }, true);
    if (res.error) return { data: null, error: res.error };
    return { data: (res.data || []).map(d => d.profiles), error: null };
  },

  async addFriend(userId: string, friendId: string): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('friends').insert({ user_id: userId, friend_id: friendId, status: 'pending' });
      if (error) return { data: null, error: error.message };
      
      // Send notification to the friend
      const { data: userData } = await supabase.from('profiles').select('username').eq('id', userId).single();
      const username = userData?.username || 'Birisi';
      await supabase.from('notifications').insert({
        user_id: friendId,
        sender_id: userId,
        type: 'friend_request',
        message: `${username} sana arkadaşlık isteği gönderdi.`,
        action_data: { friendId: userId }
      });

      return { data: null, error: null };
    }
    return post<null>('/friends', { user_id: userId, friend_id: friendId, status: 'pending' }, true);
  },

  async getFriendRequests(userId: string): Promise<ApiResult<any[]>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      // Find where someone added me and it's pending
      const { data, error } = await supabase
        .from('friends')
        .select('user_id, profiles!friends_user_id_fkey(id, username, player_tag, avatar_id, last_seen)')
        .eq('friend_id', userId)
        .eq('status', 'pending');
      if (error) return { data: null, error: error.message };
      return { data: data.map(d => d.profiles), error: null };
    }
    const res = await get<any[]>('/friends', { friend_id: `eq.${userId}`, status: 'eq.pending', select: 'user_id,profiles!friends_user_id_fkey(id,username,player_tag,avatar_id,last_seen)' }, true);
    if (res.error) return { data: null, error: res.error };
    return { data: (res.data || []).map(d => d.profiles), error: null };
  },

  async acceptFriendRequest(userId: string, friendId: string): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      // The person who sent the request is friendId, the receiver is userId
      const { error } = await supabase.from('friends')
        .update({ status: 'accepted' })
        .eq('user_id', friendId)
        .eq('friend_id', userId);
      
      if (error) return { data: null, error: error.message };
      
      // Also add the reciprocal row for bidirectional relationship
      await supabase.from('friends').insert({ user_id: userId, friend_id: friendId, status: 'accepted' });
      return { data: null, error: null };
    }
    return { data: null, error: null };
  },

  async rejectFriendRequest(userId: string, friendId: string): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('friends')
        .delete()
        .eq('user_id', friendId)
        .eq('friend_id', userId);
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    return { data: null, error: null };
  },

  async removeFriend(userId: string, friendId: string): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('friends').delete().eq('user_id', userId).eq('friend_id', friendId);
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    const headers: Record<string, string> = { Accept: 'application/json' };
    const tok = getToken();
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
    try {
      await fetch(`${API_URL}/friends?user_id=eq.${userId}&friend_id=eq.${friendId}`, { method: 'DELETE', headers });
      return { data: null, error: null };
    } catch {
      return { data: null, error: 'Silinemedi' };
    }
  },

  async submitDuelResult(duelId: number, winnerId: string, loserId: string): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.rpc('process_duel_result', {
        p_duel_id: duelId,
        p_winner_id: winnerId,
        p_loser_id: loserId
      });
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    // Local mode mock
    return { data: null, error: null };
  },

  async searchByTag(playerTag: string): Promise<ApiResult<DbUser>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase
        .from('profiles')
        .select('id,username,player_tag,avatar_id,role')
        .eq('player_tag', playerTag)
        .single();
      if (error) return { data: null, error: 'Oyuncu bulunamadı' };
      return { data: data as DbUser, error: null };
    }
    const result = await get<DbUser[]>('/profiles', { player_tag: `eq.${playerTag}`, select: 'id,username,player_tag,avatar_id,role' });
    if (!result.data?.length) return { data: null, error: 'Oyuncu bulunamadı' };
    return { data: result.data[0], error: null };
  },

  /** Health check — returns true if backend is reachable */
  async ping(): Promise<boolean> {
    try {
      const res = await fetch(`${API_URL}/`, { method: 'GET' });
      return res.ok;
    } catch {
      return false;
    }
  },

  get isLocal() { return BACKEND_MODE === 'local'; },
  get isSupabase() { return BACKEND_MODE === 'supabase'; },

  // ─── Analytics ─────────────────────────────────────────────────────────────

  async trackUserSession(
    userId: string,
    gameId: string,
    durationMs: number,
    deviceInfo: { browser: string; os: string; device_type: string }
  ): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('user_sessions').insert({
        user_id: userId,
        game_id: gameId,
        duration_ms: durationMs,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        device_type: deviceInfo.device_type,
      });
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    return post<null>('/user_sessions', { user_id: userId, game_id: gameId, duration_ms: durationMs, ...deviceInfo }, true);
  },

  async trackGuestSession(
    sessionToken: string,
    gameId: string,
    durationMs: number,
    deviceInfo: { browser: string; os: string; device_type: string }
  ): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('guest_stats').insert({
        session_token: sessionToken,
        game_id: gameId,
        duration_ms: durationMs,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        device_type: deviceInfo.device_type,
      });
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    return post<null>('/guest_stats', { session_token: sessionToken, game_id: gameId, duration_ms: durationMs, ...deviceInfo });
  },

  async getUserSessions(userId: string): Promise<ApiResult<UserSessionRow[]>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase
        .from('user_sessions')
        .select('*')
        .eq('user_id', userId)
        .order('started_at', { ascending: false })
        .limit(200);
      if (error) return { data: null, error: error.message };
      return { data: data as UserSessionRow[], error: null };
    }
    return get<UserSessionRow[]>('/user_sessions', { user_id: `eq.${userId}`, order: 'started_at.desc', limit: '200' }, true);
  },

  async getGuestStats(): Promise<ApiResult<{ unique_guests: number; total_plays: number; total_duration_ms: number; mobile_count: number; desktop_count: number; tablet_count: number; plays_last_7_days: number }>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase.from('guest_stats_summary').select('*').single();
      if (error) return { data: null, error: error.message };
      return { data: data as any, error: null };
    }
    return { data: null, error: 'Not supported in local mode' };
  },

  async getGuestTopGames(limit = 10): Promise<ApiResult<{ game_id: string; play_count: number; total_duration_ms: number }[]>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase
        .from('guest_stats')
        .select('game_id')
        .order('game_id');
      if (error) return { data: null, error: error.message };
      // Aggregate client-side
      const counts: Record<string, { play_count: number; total_duration_ms: number }> = {};
      (data as any[]).forEach((r) => {
        if (!counts[r.game_id]) counts[r.game_id] = { play_count: 0, total_duration_ms: 0 };
        counts[r.game_id].play_count++;
        counts[r.game_id].total_duration_ms += r.duration_ms || 0;
      });
      const result = Object.entries(counts)
        .map(([game_id, v]) => ({ game_id, ...v }))
        .sort((a, b) => b.play_count - a.play_count)
        .slice(0, limit);
      return { data: result, error: null };
    }
    return { data: null, error: 'Not supported in local mode' };
  },

  async getAllUserSessionsSummary(): Promise<ApiResult<any[]>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase
        .from('user_session_summary')
        .select('*');
      if (error) return { data: null, error: error.message };
      return { data: data as any[], error: null };
    }
    return { data: null, error: 'Not supported in local mode' };
  },

  // ─── Notifications ─────────────────────────────────────────────────────────

  async getNotifications(userId: string): Promise<ApiResult<any[]>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase
        .from('notifications')
        .select('*, profiles!notifications_sender_id_fkey(username, avatar_id)')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) return { data: null, error: error.message };
      return { data, error: null };
    }
    return { data: [], error: null };
  },

  async markNotificationAsRead(notificationId: number): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('notifications').update({ is_read: true }).eq('id', notificationId);
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    return { data: null, error: null };
  },

  async markAllNotificationsAsRead(userId: string): Promise<ApiResult<null>> {
    if (BACKEND_MODE === 'supabase') {
      const { supabase } = await import('./supabase');
      const { error } = await supabase.from('notifications').update({ is_read: true }).eq('user_id', userId).eq('is_read', false);
      if (error) return { data: null, error: error.message };
      return { data: null, error: null };
    }
    return { data: null, error: null };
  }
};
